import TB from '@tiger/bridge';
import { useSWR } from '@tiger/swr';
import React, { Fragment, useEffect } from 'react';
import { Navigate, Route } from 'react-router-dom';
import AnimatedRouter from 'components/AnimatedRouter';
import Card from 'components/Card';
import IBDisabledEntry from 'components/IBDisabledEntry';
import AchBank from 'components/vendor/ach/AchBank';
import ChooseBankCard from 'components/vendor/ach/ChooseBankCard';
import PlaidContinue from 'components/vendor/ach/PlaidContinue';
import UpdatePlaid from 'components/vendor/ach/UpdatePlaid';
import security from 'stores/security';
import API from 'utils/API';
import AchLayout from './Ach';
import AchComplete from './Ach/Complete';
import AchConfirm from './Ach/Confirm';
import AchFill from './Ach/Fill';
import RecurringEdit from './Ach/Recurring/Edit';
import ScheduleDetails from './Ach/Recurring/ScheduleDetails';
import ScheduleList from './Ach/Recurring/ScheduleList';
import ScheduleRecordDetails from './Ach/Recurring/ScheduleRecordDetails';
import RecurringSetup from './Ach/Recurring/Setup';
import Complete from './Complete';
import FillNotice from './FillNotice';
import { transferDepositCurrencyList } from './helper';
import Record from './Record';
import Remittance from './Remittance';
import Check from './Remittance/Check';
import SelectWay from './SelectWay';
import ContributionEnter from './SelectWay/ContributionEnter';
import Transfer from './Transfer';
import TransferComplete from './Transfer/Complete';
import FillTransfer from './Transfer/Fill';
import TransferRecord from './Transfer/Record';
import './style.scss';

const Deposit: React.FC = () => {
    const { isIRAAccount: isIRA, isAccountOpened } = security;

    useEffect(() => {
        TB.showOnlineChat(true, { page: 'Deposit_page' });
    }, []);

    // 如果是 IRA 账户，提前请求税额限额信息
    // TBUS 审核中支持入金，但该接口只有已开户账户才能调用
    useSWR(
        isIRA &&
            isAccountOpened && {
                url: API.TRADE.deposit.getIRALimits(),
                params: {
                    accountId: security.currentAccountId
                }
            },
        {
            dataChain: resp => resp.data
        }
    );

    // ib用户访问则引导至官网获取IB的登录名字和密码去ib官网操作
    if (!security.isMarscoAccount) {
        return (
            <Card page>
                <IBDisabledEntry />
            </Card>
        );
    }

    return (
        <Card page>
            <div className="deposit-root">
                <AnimatedRouter component={null} timeout={300}>
                    <Route index element={isIRA ? <ContributionEnter /> : <SelectWay />} />
                    {/* 支票汇款流程 ，只有 IRA 会走，通过支票往 IRA 账户汇款 */}
                    <Route path="check" element={<Check />} />
                    {transferDepositCurrencyList.map(currency => (
                        <Fragment key={currency}>
                            <Route path={currency} element={<Remittance defaultCurrency={currency} isIRA={isIRA} />} />
                            <Route
                                path={`${currency}/:bankId`}
                                element={<Remittance defaultCurrency={currency} isIRA={isIRA} />}
                            />
                            <Route path="*" element={<Navigate to="." replace />} />
                        </Fragment>
                    ))}
                    <Route path="notice" element={<FillNotice isIRA={isIRA} type="wire" />} />
                    <Route path="check-notice" element={<FillNotice isIRA type="check" />} />
                    <Route path="complete" element={<Complete isIRA={isIRA} type="wire" />} />
                    <Route path="check-complete" element={<Complete isIRA type="check" />} />
                    <Route path="record" element={<Record isIRA={isIRA} />} />
                    <Route path="contribution" element={<SelectWay isIRA />} />
                    <Route path="ach" element={<AchLayout />}>
                        <Route index element={<AchBank source="deposit" isIRA={isIRA} />} />
                        <Route path="fill" element={<AchFill isIRA={isIRA} />} />
                        {/* 定时入金配置页面 */}
                        <Route path="recurring" element={<RecurringSetup isIRA={isIRA} />} />
                        {/* 定时入金 schedule 列表 */}
                        <Route path="recurring/schedules" element={<ScheduleList isIRA={isIRA} />} />
                        {/* 定时入金 schedule 详情 */}
                        <Route path="recurring/schedules/:id" element={<ScheduleDetails isIRA={isIRA} />} />
                        {/* 修改定时入金的配置 */}
                        <Route path="recurring/schedules/:id/edit" element={<RecurringEdit isIRA={isIRA} />} />
                        {/* 定时入金-入金记录-详情 */}
                        <Route
                            path="recurring/schedules/:id/record"
                            element={<ScheduleRecordDetails isIRA={isIRA} />}
                        />
                        <Route path="update-plaid" element={<UpdatePlaid />} />
                        <Route path="choose-bankcard" element={<ChooseBankCard source="deposit" />} />
                        <Route path="plaid-continue" element={<PlaidContinue source="deposit" />} />
                        <Route path="confirm" element={<AchConfirm isIRA={isIRA} />} />
                        <Route path="complete" element={<AchComplete isIRA={isIRA} />} />
                        <Route path="*" element={<Navigate to="." replace />} />
                    </Route>
                    {/* IRA 选择 ACH Contribution - Individual Account Contribution  */}
                    <Route path="transfer" element={<Transfer />}>
                        <Route index element={<FillTransfer />} />
                        <Route path="complete" element={<TransferComplete />} />
                        <Route path="record" element={<TransferRecord />} />
                        <Route path="*" element={<Navigate to="." replace />} />
                    </Route>
                    <Route path="*" element={<Navigate to="." replace />} />
                </AnimatedRouter>
            </div>
        </Card>
    );
};

export default Deposit;
